import $ from "jquery"; // Import jQuery
import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import useRequiredFields from "../../../../hooks/useRequiredFields";
import DatatableFunc from "../../../Common/DatatableFunc";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import ReactDataTable from "../../../Common/ReactDataTable";

const EditOtherOvertime = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get the overtime record ID from URL
  const [inputs, setInputs] = useState({});
  const [error, setError] = useState({});
  const [staff, setStaff] = useState();
  const [distributor, setDistributor] = useState({});
  const [sr, setSr] = useState({});
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [itemLoading, setItemLoading] = useState(false);
  const [overtimeData, setOvertimeData] = useState([]);
  const [originalOvertimeData, setOriginalOvertimeData] = useState([]); // Store original data
  const [overtime, setOvertime] = useState([]);

  useEffect(() => {
    fetchInitialData();
    fetchOvertimeRecord(); // Fetch the specific overtime record to edit
    $("body").toggleClass("show-left");
  }, [id]);

  const handleOvertimeDataChange = (index, field, value) => {
    const updatedData = [...overtimeData];
    updatedData[index] = { ...updatedData[index], [field]: value };

    // Calculate total amount when hours changed (using per_hour_salary as rate)
    if (field === "ot_hours") {
      const rate = parseFloat(updatedData[index].per_hour_salary || 0);
      const hours = parseFloat(value || 0);
      updatedData[index].total_overtime_amount = (rate * hours).toFixed(2);
    }
    
    // Also recalculate if rate is manually changed
    if (field === "per_hour_salary") {
      const hours = parseFloat(updatedData[index].ot_hours || 0);
      const rate = parseFloat(value || 0);
      updatedData[index].total_overtime_amount = (rate * hours).toFixed(2);
    }

    setOvertimeData(updatedData);
  };

  const fetchOvertimeRecord = async () => {
    setLoading(true);
    try {
      // Fetch the specific overtime record with its entries
      const response = await Http.get(`transaction/${id}`);
      const overtimeRecord = response.data;
      
      // Set the form inputs with existing data
      setInputs({
        date: overtimeRecord.date,
        // Add other fields if they exist in your overtime record
      });

      // Transform the overtime entries to match the expected format
      // const transformedOvertimeData = overtimeRecord.map(entry => ({
      //   id: entry.id,
      //   staff_id: entry.hr_staff_basic_info_id,
      //   name: entry.staff_name, // Assuming this comes from the relationship
      //   date: entry.date,
      //   total_working_hours: entry.total_working_hours || "8.00",
      //   per_hour_salary: entry.rate,
      //   ot_hours: entry.total_hours,
      //   total_overtime_amount: entry.total_amount,
      //   remarks: entry.remarks || ""
      // }));

      setOvertimeData(overtimeRecord);
      // setOriginalOvertimeData(transformedOvertimeData); // Keep original data for comparison
      
    } catch (error) {
      console.error("Error fetching overtime record:", error);
      Swal.fire("Error!", "Failed to load overtime record.", "error");
      // navigate("/other-overtime");
    } finally {
      setLoading(false);
    }
  };

  const fetchInitialData = async () => {
    try {
      // Get available staff for adding more entries
      Http.get("staff").then((response) => {
        setStaff(response.data);
      });

      setDistributor({
        id: 1,
        name: "Main Office",
        business_type: "ho"
      });

    } catch (error) {
      console.error("Fetch error:", error);
    }
  };

  const addMoreList = async (staffId) => {
    setItemLoading(true);
    try {
      // Check if staff member is already in overtime data
      const existingEntry = overtimeData.find(entry => entry.staff_id === staffId);

      if (existingEntry) {
        Swal.fire("Warning!", "This staff member is already in the overtime list!", "warning");
        setItemLoading(false);
        return;
      }

      // Get staff data from API
      const response = await Http.get('get-others-overtime-data/' + staffId);
      const selectedStaff = response.data;

      if (!selectedStaff) {
        Swal.fire("Error!", "Staff member not found!", "error");
        setItemLoading(false);
        return;
      }

      // Create new overtime entry
      const newOvertimeEntry = {
        id: Date.now(), // Generate unique ID for new entries
        staff_id: staffId,
        name: selectedStaff.name,
        date: inputs.date || new Date().toISOString().split('T')[0],
        total_working_hours: selectedStaff.total_working_hours || "8.00", 
        per_hour_salary: selectedStaff.per_hour_salary || "0.00",
        ot_hours: "0.00",
        total_overtime_amount: "0.00",
        remarks: "",
        isNew: true // Flag to identify new entries
      };

      // Add to overtime data
      setOvertimeData(prev => [...prev, newOvertimeEntry]);

    } catch (error) {
      console.error("Error adding staff to overtime:", error);
      Swal.fire("Error!", "Failed to add staff member.", "error");
    } finally {
      setItemLoading(false);
    }
  };

  const removeOvertimeEntry = (index) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, remove it!"
    }).then((result) => {
      if (result.isConfirmed) {
        const entryToRemove = overtimeData[index];
        const updatedOvertimeData = overtimeData.filter((_, i) => i !== index);
        
        // If it's an existing entry (not new), mark it for deletion
        if (!entryToRemove.isNew) {
          entryToRemove.isDeleted = true;
          // Keep deleted entries in a separate state or array for backend processing
        }
        
        setOvertimeData(updatedOvertimeData);
        Swal.fire("Removed!", "Staff member has been removed from overtime list.", "success");
      }
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const formSubmit = async () => {
    setLoading(true);
    
    // Validate that at least one staff member is added
    if (overtimeData.length === 0) {
      Swal.fire("Warning!", "Please add at least one staff member for overtime!", "warning");
      setLoading(false);
      return;
    }

    // Validate overtime data
    const invalidEntries = overtimeData.filter(entry =>
      !entry.ot_hours || parseFloat(entry.ot_hours) <= 0
    );

    if (invalidEntries.length > 0) {
      Swal.fire("Warning!", "Please fill in valid overtime hours and rates for all staff members!", "warning");
      setLoading(false);
      return;
    }

    try {
      // Prepare data for submission
      const submitData = {
        date: inputs.date,
        overtime_entries: overtimeData.map(item => ({
          id: item.isNew ? null : item.id, // null for new entries, existing ID for updates
          hr_staff_basic_info_id: item.staff_id,
          date: inputs.date,
          rate: item.per_hour_salary,
          total_hours: item.ot_hours,
          total_amount: item.total_overtime_amount,
          remarks: item.remarks || "",
          action: item.isNew ? 'create' : 'update' // Flag to indicate action
        })),
        // Include deleted entries if you're tracking them
        deleted_entries: [] // You can track deleted entries here if needed
      };

      const response = await Http.put(`other-overtime/${id}`, submitData);
      
      navigate("/other-overtime", {
        state: {
          message: response.data.message || "Overtime updated successfully",
          type: response.data.type || "success",
        },
      });
      
    } catch (error) {
      console.error("Submit error:", error);
      setError(
        error.response?.data?.errors || {
          general: "Error updating overtime entries",
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const displayError = (fieldName) => {
    if (error && error[fieldName]) {
      return <div className="text-danger small">{error[fieldName]}</div>;
    }
    return null;
  };

  const cols = [
    { field: "name", header: "Staff Name" },
  ];

  const actionTemplate = (item, deleteData) => {
    return (
      <td className="text-right">
        <button
          className="btn btn-success btn-sm"
          onClick={() => addMoreList(item.id)}
          disabled={itemLoading}
        >
          {itemLoading ? (
            <i className="fa fa-spinner fa-spin"></i>
          ) : (
            <i className="fa fa-plus"></i>
          )}
        </button>
      </td>
    );
  };

  // Calculate total overtime amount
  const totalOvertimeAmount = overtimeData.reduce((sum, entry) =>
    sum + parseFloat(entry.total_overtime_amount || 0), 0
  );

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Edit Other Overtime</h5>
        </div>
        <div className="am-pagebody row d-flex justify-content-evenly">
          <div className="card pd-20 pd-sm-40 col-8">
            <h6 className="card-body-title mg-sm-b-30 text-center">
              Edit Other Overtime Form
            </h6>
            <hr></hr>

            {loading ? (
              <ProgressSpinner />
            ) : (
              <>
                <div className="row">
                  <div className="col-lg-4">
                    <label>
                      Date <i className="fa fa-star required"></i>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      name="date"
                      value={inputs.date || ""}
                      onChange={handleChange}
                      placeholder="Input Date"
                    />
                    {displayError("date")}
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="card-solid">
                    <div className="card-header with-border text-center">
                      <h5 className="card-title mb-0">
                        Staff Overtime Details{" "}
                        <i className="fa fa-star required"></i>
                      </h5>
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-striped table-bordered">
                          <thead className="table-dark">
                            <tr>
                              <th>Staff Name</th>
                              <th>Per Hour</th>
                              <th>OT Hours</th>
                              <th>Total Amount</th>
                              <th>Remarks</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {overtimeData.length === 0 ? (
                              <tr>
                                <td colSpan="6" className="text-center text-muted py-4">
                                  <i className="fa fa-info-circle mb-2"></i>
                                  <br />
                                  No staff members added for overtime yet.
                                  <br />
                                  Use the &quot;+&quot; button from the Available Items section to add staff members.
                                </td>
                              </tr>
                            ) : (
                              overtimeData.map((staffMember, index) => (
                                <tr key={staffMember.id}>
                                  <td>
                                    <strong>{staffMember.name}</strong>
                                    {staffMember.isNew && (
                                      <span className="badge badge-success ml-2">New</span>
                                    )}
                                  </td>
                                  <td>
                                    <span className="text-muted">
                                      ৳
                                      {parseFloat(
                                        staffMember.per_hour_salary || 0
                                      ).toFixed(2)}
                                    </span>
                                  </td>
                                  <input
                                    type="hidden"
                                    value={staffMember.total_working_hours || ""}
                                  />
                                  <td>
                                    <input
                                      type="number"
                                      step="0.25"
                                      className="form-control form-control-sm"
                                      value={staffMember.ot_hours || ""}
                                      onChange={(e) =>
                                        handleOvertimeDataChange(
                                          index,
                                          "ot_hours",
                                          e.target.value
                                        )
                                      }
                                      placeholder="OT Hours"
                                    />
                                  </td>
                                  <td>
                                    <div className="input-group input-group-sm">
                                      <div className="input-group-prepend">
                                        <span className="input-group-text">৳</span>
                                      </div>
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={staffMember.total_overtime_amount || "0"}
                                        readOnly
                                      />
                                    </div>
                                  </td>
                                  <td>
                                    <textarea
                                      className="form-control form-control-sm"
                                      rows="2"
                                      value={staffMember.remarks || ""}
                                      onChange={(e) =>
                                        handleOvertimeDataChange(
                                          index,
                                          "remarks",
                                          e.target.value
                                        )
                                      }
                                      placeholder="Remarks"
                                    />
                                  </td>
                                  <td>
                                    <button
                                      className="btn btn-danger btn-sm"
                                      onClick={() => removeOvertimeEntry(index)}
                                      title="Remove from overtime list"
                                    >
                                      <i className="fa fa-trash"></i>
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-3">
                    <button
                      className="btn btn-primary btn-block mg-b-10"
                      onClick={formSubmit}
                      disabled={loading}
                    >
                      <i className="fa fa-save mg-r-10"></i> Update Overtime
                    </button>
                  </div>
                  <div className="col-3">
                    <button
                      className="btn btn-secondary btn-block mg-b-10"
                      onClick={() => navigate("/other-overtime")}
                    >
                      <i className="fa fa-arrow-left mg-r-10"></i> Back to List
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div
            className="card pd-20 pd-sm-40 col-3"
            style={{ backgroundColor: "#ffffffc7" }}
          >
            <h6 className="card-body-title mg-sm-b-30 text-center">
              Available Staff Members
            </h6>
            <hr></hr>
            <ReactDataTable
              cols={cols}
              receiveData={staff}
              isLoading={loading}
              actionTemplate={actionTemplate}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditOtherOvertime;